import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from 'src/environments/environment';

export interface ArchiveFile {
    filename: string;
    path: string;
    full_path: string;
    year: number;
    month: number;
    month_name: string;
    period_label: string;
    file_size: number;
    file_size_formatted: string;
    record_count: number | null;
    modified_at: string;
}

export interface ArchiveSession {
    id: number;
    archive_file: string;
    archive_path: string;
    year: number;
    month: number;
    period_label: string;
    records_imported: number;
    file_size_formatted: string;
    loaded_by: string;
    loaded_at: string;
    expires_at: string;
    time_remaining: string | null;
}

export interface ArchiveSessionHistory {
    id: number;
    archive_file: string;
    period_label: string;
    loaded_by_user_name: string;
    records_imported: number;
    file_size_formatted: string;
    loaded_at: string;
    expires_at: string;
    unloaded_at: string | null;
    status: string;
    status_label: string;
}

export interface ArchiveStatus {
    has_active_session: boolean;
    session: ArchiveSession | null;
}

export interface ArchiveLogItem {
    id: number;
    original_id: number;
    user_id: number;
    user_name: string;
    directie_id: number | null;
    directie_name: string | null;
    compartiment_id: number | null;
    compartiment_name: string | null;
    functie_id: number | null;
    functie_name: string | null;
    module: string;
    action: string;
    action_label: string;
    actor_type: string;
    actor_type_label: string;
    source: string;
    source_label: string;
    model_type: string;
    short_model_type: string;
    model_id: number;
    model_name: string | null;
    old_values: any | null;
    new_values: any | null;
    result: string;
    result_label: string;
    error_message: string | null;
    ip_address: string | null;
    user_agent: string | null;
    request_id: string | null;
    has_diff: boolean;
    display_date: string;
    original_created_at: string;
    original_updated_at: string;
}

export interface MetaOption {
    value: string;
    label: string;
}

export interface LoadArchiveResult {
    success: boolean;
    error?: string;
    session_id?: number;
    records_imported?: number;
    expires_at?: string;
    period_label?: string;
}

export interface UnloadArchiveResult {
    success: boolean;
    error?: string;
    records_deleted?: number;
}

@Injectable({
    providedIn: 'root'
})
export class AuditArchivesService {
    private basePath = environment.interop.basePath;
    private apiUrl = `${this.basePath}api/audit-archives`;

    constructor(private http: HttpClient) {}

    // ==================== MANAGEMENT ====================

    /**
     * Obține lista arhivelor disponibile pe disk
     */
    getAvailableArchives(): Observable<{ success: boolean; data: ArchiveFile[] }> {
        return this.http.get<{ success: boolean; data: ArchiveFile[] }>(`${this.apiUrl}/available`);
    }

    /**
     * Obține statusul sesiunii curente
     */
    getStatus(): Observable<{ success: boolean; data: ArchiveStatus }> {
        return this.http.get<{ success: boolean; data: ArchiveStatus }>(`${this.apiUrl}/status`);
    }

    /**
     * Încarcă o arhivă în baza de date temporară
     */
    loadArchive(archivePath: string): Observable<LoadArchiveResult> {
        return this.http.post<LoadArchiveResult>(`${this.apiUrl}/load`, {
            archive_path: archivePath
        });
    }

    /**
     * Descarcă (șterge) arhiva curentă
     */
    unloadArchive(): Observable<UnloadArchiveResult> {
        return this.http.delete<UnloadArchiveResult>(`${this.apiUrl}/unload`);
    }

    /**
     * Obține istoricul sesiunilor de încărcare
     */
    getSessionHistory(params: any): Observable<any> {
        return this.http.get<any>(`${this.apiUrl}/sessions`, {
            params: this.buildParams(params)
        });
    }

    // ==================== VIZUALIZARE DATE ====================

    /**
     * Obține lista de audit logs din arhiva încărcată (paginată, pentru DataTable)
     */
    getArchiveLogs(params: any): Observable<any> {
        return this.http.get<any>(`${this.apiUrl}/logs`, {
            params: this.buildParams(params)
        });
    }

    /**
     * Obține detaliile unui log din arhivă
     */
    getArchiveLogDetail(logId: number): Observable<{ success: boolean; data: ArchiveLogItem }> {
        return this.http.get<{ success: boolean; data: ArchiveLogItem }>(`${this.apiUrl}/logs/${logId}`);
    }

    /**
     * Export CSV din arhiva încărcată
     */
    exportCsv(params: any): Observable<Blob> {
        return this.http.get(`${this.apiUrl}/logs/export/csv`, {
            params: this.buildParams(params),
            responseType: 'blob'
        });
    }

    // ==================== META ====================

    /**
     * Obține lista modulelor din arhiva încărcată
     */
    getModules(): Observable<{ success: boolean; data: MetaOption[] }> {
        return this.http.get<{ success: boolean; data: MetaOption[] }>(`${this.apiUrl}/meta/modules`);
    }

    /**
     * Obține lista acțiunilor disponibile
     */
    getActions(): Observable<{ success: boolean; data: MetaOption[] }> {
        return this.http.get<{ success: boolean; data: MetaOption[] }>(`${this.apiUrl}/meta/actions`);
    }

    // ==================== HELPERS ====================

    /**
     * Download file din blob
     */
    downloadFile(blob: Blob, filename: string): void {
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
    }

    /**
     * Build HttpParams din object
     */
    private buildParams(params: any): HttpParams {
        let httpParams = new HttpParams();
        if (params) {
            Object.keys(params).forEach(key => {
                if (params[key] !== null && params[key] !== undefined && params[key] !== '') {
                    httpParams = httpParams.set(key, params[key]);
                }
            });
        }
        return httpParams;
    }
}
